"use client";

import * as React from "react";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/logo";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { cn } from "@/lib/utils";
import { marketingNavLinks } from "@/lib/site-config";

function SiteHeader() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
        <NextLink href="/" className="flex items-center text-foreground">
          <Logo height={44} />
        </NextLink>

        <nav className="hidden items-center gap-1 md:flex">
          {marketingNavLinks.map((link) => (
            <NextLink
              key={link.href}
              href={link.href}
              aria-current={pathname === link.href ? "page" : undefined}
              className={cn(
                "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                pathname === link.href
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {link.label}
            </NextLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <ThemeToggle />
          <Button variant="ghost" size="sm" asChild>
            <NextLink href="/login">Log in</NextLink>
          </Button>
          <Button variant="primary" size="sm" asChild>
            <NextLink href="/#pricing">Get started</NextLink>
          </Button>
        </div>

        <button
          type="button"
          className="inline-flex cursor-pointer items-center justify-center rounded-md p-2 text-foreground md:hidden"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((open) => !open)}
        >
          {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </div>

      {mobileOpen && (
        <nav className="border-t border-border bg-card px-4 pb-4 md:hidden">
          <div className="flex flex-col gap-1 pt-2">
            {marketingNavLinks.map((link) => (
              <NextLink
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                {link.label}
              </NextLink>
            ))}
            <div className="mt-2 flex items-center gap-2 border-t border-border pt-3">
              <Button variant="ghost" size="sm" asChild className="flex-1">
                <NextLink href="/login">Log in</NextLink>
              </Button>
              <Button variant="primary" size="sm" asChild className="flex-1">
                <NextLink href="/#pricing">Get started</NextLink>
              </Button>
              <ThemeToggle />
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}

export { SiteHeader };
